import * as React from "react"

const isiDividerIcon =
  "/figma/shared/isi-divider.svg"
const isiFrameClassName =
  "mx-auto w-full max-w-[1200px] md:w-[calc(100%-48px)]"
const isiSheetClosedClassName =
  "pointer-events-none translate-y-[calc(100%-127px)] md:translate-y-[calc(100%-140px)]"

type IsiShellProps = {
  children: React.ReactNode
  className?: string
}

export type IsiTextProps = {
  children: React.ReactNode
  className?: string
}

type IsiActionButtonProps = {
  actionLabel: string
  buttonRef?: React.Ref<HTMLButtonElement>
  isClose?: boolean
  onClick: () => void
}

function joinClasses(...classes: Array<string | undefined>) {
  return classes.filter(Boolean).join(" ")
}

function IsiToggleIcon({
  className,
  isClose = false,
}: {
  className?: string
  isClose?: boolean
}) {
  return (
    <svg
      aria-hidden="true"
      className={joinClasses(
        "h-[17px] w-[17px] shrink-0 text-black transition-transform duration-300",
        isClose ? "rotate-45" : undefined,
        className,
      )}
      fill="none"
      viewBox="0 0 16 16"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="8" cy="8" r="7.5" stroke="currentColor" />
      <path
        d="M8 4.4174V11.5284"
        stroke="currentColor"
        strokeLinecap="round"
      />
      <path
        d="M11.3665 7.8913H4.3571"
        stroke="currentColor"
        strokeLinecap="round"
      />
    </svg>
  )
}

function IsiActionButton({
  actionLabel,
  buttonRef,
  isClose = false,
  onClick,
}: IsiActionButtonProps) {
  return (
    <button
      ref={buttonRef}
      aria-label={actionLabel}
      className="flex items-center gap-[5px] text-black cursor-pointer"
      onClick={onClick}
      type="button"
    >
      <IsiToggleIcon isClose={isClose} />
      <span className="font-sans text-[12px] font-bold uppercase leading-[24px] underline decoration-black underline-offset-[2px] md:text-[16px]">
        {actionLabel}
      </span>
    </button>
  )
}

export type IsiInlineShellProps = IsiShellProps

export const IsiInlineShell = React.forwardRef<HTMLElement, IsiInlineShellProps>(
  ({ children, className }, ref) => {
    return (
      <section
        ref={ref}
        aria-label="Important safety information"
        className={joinClasses("w-full bg-white pb-[20px] text-ink-950", className)}
      >
        <div
          className={joinClasses(
            isiFrameClassName,
            "px-[12px] py-[12px] md:px-0 md:py-[24px]",
          )}
        >
          {children}
        </div>
      </section>
    )
  },
)

IsiInlineShell.displayName = "IsiInlineShell"

export type IsiTrayShellProps = {
  onOpen: () => void
  preview: React.ReactNode
  title?: string
}

export const IsiTrayShell = React.forwardRef<HTMLButtonElement, IsiTrayShellProps>(
  (
    {
      onOpen,
      preview,
      title = "Important Safety Information",
    },
    ref,
  ) => {
    return (
      <div
        aria-label="Important safety information tray"
        className="fixed inset-x-0 bottom-0 z-40 bg-white text-ink-950 shadow-[0px_-4px_15px_0px_rgba(0,0,0,0.25)] md:shadow-[0px_-4px_7.5px_rgba(0,0,0,0.3)]"
        role="region"
      >
        <div className="bg-accent-500">
          <div
            className={joinClasses(
              isiFrameClassName,
              "flex h-[30px] items-center justify-between px-[12px] md:h-[33px] md:px-0",
            )}
          >
            <p className="font-sans text-[14px] font-bold uppercase leading-[21px] tracking-[-0.154px] text-black md:text-[18px] md:leading-[30px] md:tracking-normal">
              {title}
            </p>
            <IsiActionButton
              actionLabel="Open"
              buttonRef={ref}
              onClick={onOpen}
            />
          </div>
        </div>

        <div
          aria-hidden="true"
          className={joinClasses(
            isiFrameClassName,
            "h-[97px] overflow-hidden px-[10px] py-[16px] md:h-[107px] md:px-0 md:py-[24px]",
          )}
        >
          {preview}
        </div>
      </div>
    )
  },
)

IsiTrayShell.displayName = "IsiTrayShell"

export type IsiSheetShellProps = IsiShellProps & {
  closeButtonRef?: React.Ref<HTMLButtonElement>
  isOpen: boolean
  onClose: () => void
  sheetRef?: React.Ref<HTMLDivElement>
  title?: string
}

export const IsiSheetShell: React.FC<IsiSheetShellProps> = ({
  children,
  className,
  closeButtonRef,
  isOpen,
  onClose,
  sheetRef,
  title = "Important Safety Information",
}) => {
  return (
    <div
      aria-labelledby="consumer-isi-sheet-title"
      aria-modal="true"
      className={joinClasses(
        "fixed inset-0 z-50 overflow-hidden bg-white text-ink-950 transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]",
        isOpen ? "translate-y-0" : isiSheetClosedClassName,
        className,
      )}
      ref={sheetRef}
      role="dialog"
      tabIndex={-1}
    >
      <div className="flex h-full flex-col">
        <div className="bg-accent-500">
          <div
            className={joinClasses(
              isiFrameClassName,
              "flex h-[37px] items-center justify-between px-[12px] md:h-[33px] md:px-0",
            )}
          >
            <h2
              className="font-sans text-isi font-bold uppercase leading-[22.5px] tracking-[-0.165px] text-black md:text-[18px] md:leading-[30px] md:tracking-normal"
              id="consumer-isi-sheet-title"
            >
              {title}
            </h2>
            <IsiActionButton
              actionLabel="Close"
              buttonRef={closeButtonRef}
              isClose
              onClick={onClose}
            />
          </div>
        </div>

        <div className="min-h-0 overflow-y-auto">
          <div
            className={joinClasses(
              isiFrameClassName,
              "px-[12px] py-[12px] md:px-0 md:py-[24px]",
            )}
          >
            {children}
          </div>
        </div>
      </div>
    </div>
  )
}

export const IsiLead: React.FC<IsiTextProps> = ({ children, className }) => {
  return (
    <p
      className={[
        "font-sans text-isi font-bold text-isi-copy",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {children}
    </p>
  )
}

export const IsiHeading: React.FC<IsiTextProps> = ({ children, className }) => {
  return (
    <h3
      className={[
        "font-sans text-isi font-bold text-isi-heading",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {children}
    </h3>
  )
}

export const IsiBody: React.FC<IsiTextProps> = ({ children, className }) => {
  return (
    <p
      className={[
        "font-sans text-isi text-isi-copy",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {children}
    </p>
  )
}

export const IsiWarning: React.FC<IsiTextProps> = ({ children, className }) => {
  return (
    <div
      className={[
        "border-2 border-isi-copy p-8",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <IsiLead>{children}</IsiLead>
    </div>
  )
}

export const IsiList: React.FC<IsiTextProps> = ({ children, className }) => {
  return (
    <ul
      className={[
        "list-disc pl-[22.5px] font-sans text-isi text-isi-copy",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {children}
    </ul>
  )
}

export const IsiLink: React.FC<
  IsiTextProps & { href?: string; target?: string }
> = ({ children, className, href, target }) => {
  const linkClassName = [
    "font-sans text-isi font-bold text-isi-link underline decoration-[from-font] underline-offset-[2px]",
    className,
  ]
    .filter(Boolean)
    .join(" ")

  if (!href) {
    return <span className={linkClassName}>{children}</span>
  }

  const isExternalLink = href.startsWith("http")

  return (
    <a
      className={linkClassName}
      href={href}
      rel={isExternalLink ? "noreferrer" : undefined}
      target={target ?? (isExternalLink ? "_blank" : undefined)}
    >
      {children}
    </a>
  )
}

export const IsiSup: React.FC<IsiTextProps> = ({ children, className }) => {
  return (
    <sup
      className={[
        "relative -top-[0.35em] text-[9.675px] leading-none",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {children}
    </sup>
  )
}

export const IsiDivider: React.FC = () => {
  return (
    <div className="flex h-5 items-center gap-4">
      <div className="h-0 flex-1 border-t-2 border-isi-divider" />
      <img alt="" aria-hidden="true" className="h-5 w-5" src={isiDividerIcon} />
      <div className="h-0 flex-1 border-t-2 border-isi-divider" />
    </div>
  )
}
